import React, { useEffect } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Stack 
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { FormTextField, FormTagsInput } from '@personal-dashboard/ui';

interface Recipe {
  id: string;
  title: string;
  instructions?: string;
  ingredients: string[];
}

interface RecipeFormValues {
  title: string;
  instructions: string;
  ingredients: string[];
}

interface RecipeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: RecipeFormValues) => Promise<void> | void;
  recipe: Recipe | null;
}

const RecipeFormDialog: React.FC<RecipeFormDialogProps> = ({ open, onClose, onSubmit, recipe }) => {
  const methods = useForm<RecipeFormValues>({
    defaultValues: {
      title: '',
      instructions: '',
      ingredients: [],
    },
  });

  const { reset, handleSubmit } = methods;

  useEffect(() => {
    if (open) {
      if (recipe) {
        reset({
          title: recipe.title,
          instructions: recipe.instructions || '',
          ingredients: recipe.ingredients || [],
        });
      } else {
        reset({
          title: '',
          instructions: '',
          ingredients: [],
        });
      }
    }
  }, [open, recipe, reset]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {recipe ? 'Edit Recipe' : 'Add Recipe'}
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Stack spacing={3}>
              <FormTextField
                name="title"
                label="Recipe Title"
                fullWidth
                required
              />
              <FormTextField
                name="instructions"
                label="Instructions"
                fullWidth
                multiline
                rows={4}
              />
              <FormTagsInput
                name="ingredients"
                label="Ingredients"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default RecipeFormDialog;
export type { RecipeFormValues };
